/**
 * Refund — a provider-refund ledger under the order; one writer: the
 * refund consumer (webhook/poll), which also decrements `consumed` per
 * line in the same txn — the doc-id-as-once-guard matters more here, with
 * no reReserve path to double-check against.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/orders/{orderId}/refunds/{providerRefundId}`
 * — member read behind `tickets.reports.view`, denied until `mfa: true`.
 * NO client write path — money is written only by the refund consumer
 * (webhook/poll). The nested path already carries both the event and the
 * order this refund settles; orderId is not a body field.
 *
 * `status: 'failed'` is the one operator-visible terminal state, and
 * `refundPoll`'s own leg queries exactly it (`collectionGroup('refunds').where
 * ('status', '==', 'failed')`, the deployed `refunds.status` override) and
 * alarms per row — a claim whose money may already have moved is never
 * silently abandoned. `failure` names which of the seven ways it got there.
 *
 * The doc id is the once-guard and is never the provider's answer: a refund
 * this deployment initiates is keyed `claim-{sha256(orderId + sorted
 * ticketIds)}`, minted BEFORE the provider is called, and one observed in the
 * provider's own dashboard is keyed `psp-{observedCents}`. Keying on the
 * provider's refund id would mean the guard exists only after the money has
 * already moved; `providerRefundId` below carries that id as a field instead.
 *
 * Beside the money it moves, a settled row owns a second artifact: the
 * Rechnungskorrektur `correctionNumber` names, minted from the same workspace
 * invoice series as the order's own number and stored beside the receipt the
 * order's invoice stays printed on.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { Cents, PositiveCents } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

// Fields justified:
//   ticketIds        — the refunded set, and the exclusivity key: a later
//                      producer subtracts every ticket any existing row already
//                      names before it derives its own working set, which is
//                      what stops two overlapping selections refunding one
//                      ticket twice
//   amount           — the cents this row moved AND recorded: the order's
//                      `refundedAmount` advanced by exactly this figure, the
//                      seats came back for exactly these tickets
//   unrecordedAmount — the cents the provider moved that this order had no
//                      gross left to record, which therefore exist in no
//                      counter anywhere: `amount` is what the books show and
//                      only their sum is what actually left. Absent on every
//                      row whose whole figure fitted
//   initiatedBy      — which of the three origins produced it
//   status           — the lifecycle, `claiming` included: the row exists
//                      before the provider is called
//   providerRefundId — the provider's own id, learned only after the call
//   failure          — why a `failed` row needs a human
//   providerRefusal  — the last thing the provider said when it turned this
//                      refund down, code AND message: the refund poll's
//                      failed-row alarm and the consumer's own per-attempt
//                      warn both read it, and neither can tell an operator
//                      what to fix from "the provider said no". It is written
//                      on a row that is still `claiming` and retrying, not
//                      only on a terminal one, because a refusal the ladder
//                      will retry is exactly the refusal that went unrecorded
//                      while an order sat stuck for hours. Message beside
//                      code because the code degrades to `bad_request` while
//                      the message keeps the meaning
//   batchId          — which batch's marker owns a `claiming` row, so a retry
//                      resumes its own claim instead of minting a second one
//   settledAt        — the stamp the correction document is dated and filed by;
//                      the correction trigger reads it for both
//   correctionNumber — the once-guard of that document: a row carrying one has
//                      already consumed a number from the workspace series
// Excluded:
//   orderId / eventId — the nested path carries both
//   createdAt — the document's own createTime is what the poll's stale bound
//     already measures against
export const RefundSchema = z.object({
	ticketIds: z.array(z.string()),
	amount: Cents,
	unrecordedAmount: PositiveCents.nullish().describe(
		"the cents this refund moved beyond what the order could record — the difference between what the provider was asked for and the clamped figure `amount` carries, present only on an overpaid row and the only place that difference is written down at all",
	),
	initiatedBy: z
		.union([z.literal("customer"), z.literal("psp"), z.string()])
		.describe(
			"who this refund originated with — customer for the buyer's own self-service path, psp for a refund performed in the provider's own dashboard and observed here, otherwise the member uid that initiated it",
		),
	status: z
		.enum(["claiming", "pending", "settled", "failed"])
		.describe(
			"the refund’s lifecycle — claiming while this row holds its tickets against every other producer and the provider has not answered yet, pending once the money moved and the provider has still to confirm it, then settled, or failed for a row an operator must reconcile against the provider's own dashboard; a claiming or failed row still holds its tickets, so no later producer refunds them",
		),
	providerRefundId: z
		.string()
		.nullish()
		.describe(
			"the provider's own id for this refund, absent while the row is claiming because the provider has not answered yet",
		),
	// `headroomLost` and `overpaid` are the two halves of one condition — the
	// claimed amount outgrowing the order's own gross — and they are separate
	// tokens because the operator's answer is opposite on each. `headroomLost`
	// is caught BEFORE the provider is re-asked, so nothing at all is recorded:
	// no `refundedAmount`, no cancellation, no seat. `overpaid` is caught while
	// the money is already gone, so the give-back is recorded to what fits and
	// `unrecordedAmount` carries the rest. One token for both told an operator
	// reading a `headroomLost` row that a clamped give-back had landed when
	// none had.
	failure: z
		.enum([
			"unsettledPastBound",
			"settleRefused",
			"redeemedAfterClaim",
			"headroomLost",
			"overpaid",
			"attemptsExhausted",
			"providerRefused",
		])
		.nullish()
		.describe(
			"why this refund needs a human — unsettledPastBound when the provider's cumulative refunded total still fell short of this row past the stale bound, settleRefused when the provider answered but the recording transaction refused a fact no retry revises, redeemedAfterClaim when a ticket this claim holds was admitted at the door after the claim was taken, headroomLost when the claim's stored amount stopped fitting the order's remaining gross and the provider was never re-asked so nothing was recorded, overpaid when the provider moved more than the order could record and the give-back was recorded to the clamped figure with the excess on unrecordedAmount, attemptsExhausted when the retry ladder ran out while this claim still held its tickets, providerRefused when the provider named a fact about this order that no later attempt can reverse — `providerRefusal` carries what it said",
		),
	providerRefusal: z
		.object({
			code: z
				.string()
				.nullish()
				.describe(
					"the provider's machine-readable token for this refusal, absent when it answered in no shape this deployment could read one from",
				),
			message: z
				.string()
				.describe(
					"the provider's own words, verbatim — response-derived and never a credential, and the half an operator can usually act on when the code has degraded to a generic token",
				),
		})
		.nullish()
		.describe(
			"the last refusal the provider gave for this refund, whether or not it ended the row: a claiming row carries it while the ladder keeps asking, so an order stuck behind a refusal is legible before it becomes terminal, and a row that later settled keeps it as the record of what stood in the way. Absent on a row the provider never turned down",
		),
	batchId: z.string().nullish(),
	settledAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"when the settle transaction closed the books on this row — the § 17 Abs. 1 UStG correction period, and the issue date its correction document is pinned to, so a re-drive renders identical bytes",
		),
	correctionNumber: z
		.string()
		.nullish()
		.describe(
			"the § 14 UStG sequential number of this refund’s Rechnungskorrektur, minted once from the same workspace series as Order.invoiceNumber — null while the correction trigger has not run, and permanently on a refund of an order that never carried an invoice",
		),
});
export type Refund = z.infer<typeof RefundSchema>;

export const Refund = entity<Actor>()({
	schema: RefundSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/orders/{orderId}/refunds/{providerRefundId}",
		}),
	);
